import React, { useEffect, useState } from 'react';
import { Card } from '../components/Card';
import { Header } from '../components/Header';
import { Icon } from '../components/Icon';
import { Button } from '../components/Button';
import { apiClient, Session } from '../api/client';
import { useTranslation } from 'react-i18next';
import './DeviceManagementScreen.css';

interface DeviceManagementScreenProps {
  onClose: () => void;
}

export const DeviceManagementScreen: React.FC<DeviceManagementScreenProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState<number | null>(null);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  useEffect(() => {
    loadSessions();
  }, []);

  // Swipe to close
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isRightSwipe) {
      onClose();
    }
  };

  const loadSessions = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getSessions();
      setSessions(response.sessions);
    } catch (error) {
      console.error('Error loading sessions:', error);
      
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeSession = async (sessionId: number) => {
    if (!confirm(t('devices.revokeConfirm'))) return;

    try {
      setRevoking(sessionId);
      
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.impactOccurred('medium');
      }

      await apiClient.revokeSession(sessionId);
      await loadSessions();
      
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('success');
      }
    } catch (error) {
      console.error('Error revoking session:', error);
      
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('error');
      }
    } finally {
      setRevoking(null);
    }
  };

  const handleRevokeAllOther = async () => {
    if (!confirm(t('devices.revokeAllOtherConfirm'))) return;

    try {
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.impactOccurred('heavy');
      }

      await apiClient.revokeAllOtherSessions();
      await loadSessions();
      
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('success');
      }
    } catch (error) {
      console.error('Error revoking sessions:', error);
      
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('error');
      }
    }
  };

  const getDeviceIcon = (deviceType: string): string => {
    switch (deviceType) {
      case 'mobile': return 'smartphone';
      case 'desktop': return 'monitor';
      case 'tablet': return 'tablet';
      default: return 'help-circle';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const otherSessionsCount = sessions.filter(s => !s.is_current).length;

  return (
    <div 
      className="device-management"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <Header title={t('devices.title') || 'Устройства'} onBack={onClose} />

      {!loading && otherSessionsCount > 0 && (
        <div className="device-management__actions">
          <Button
            variant="danger"
            onClick={handleRevokeAllOther}
            fullWidth
          >
            {t('devices.revokeAllOther')} ({otherSessionsCount})
          </Button>
        </div>
      )}

      <div className="device-management__list">
        {loading ? (
          <div className="device-management__loading">
            <Icon name="loader" size={32} />
            <span>{t('devices.loading')}</span>
          </div>
        ) : sessions.length === 0 ? (
          <div className="device-management__empty">
            <Icon name="smartphone" size={48} color="var(--color-text-tertiary)" />
            <p>{t('devices.noSessions')}</p>
          </div>
        ) : (
          sessions.map((session) => (
            <Card key={session.id} padding="md" className="device-item neo-raised">
              <div className="device-item__content">
                <div className="device-item__icon-wrapper">
                  <div className="device-item__icon neo-inset">
                    <Icon 
                      name={getDeviceIcon(session.device_type)} 
                      size={28} 
                      color="var(--color-primary)"
                    />
                  </div>
                </div>
                
                <div className="device-item__info">
                  <div className="device-item__name">
                    {session.device_name || t('devices.unknownDevice')}
                    {session.is_current && (
                      <span className="device-item__badge">{t('devices.current')}</span>
                    )}
                  </div>
                  <div className="device-item__details">
                    <div className="device-item__detail">
                      <Icon name="calendar" size={14} color="var(--color-text-secondary)" />
                      <span>{t('devices.loggedIn')}: {formatDate(session.created_at)}</span>
                    </div>
                    <div className="device-item__detail">
                      <Icon name="clock" size={14} color="var(--color-text-secondary)" />
                      <span>{t('devices.lastActive')}: {formatDate(session.last_activity)}</span>
                    </div>
                    <div className="device-item__detail">
                      <Icon name="globe" size={14} color="var(--color-text-secondary)" />
                      <span>IP: {session.ip_address}</span>
                    </div>
                  </div>
                </div>

                {!session.is_current && (
                  <button
                    className="device-item__delete neo-raised"
                    onClick={() => handleRevokeSession(session.id)}
                    disabled={revoking === session.id}
                  >
                    <Icon 
                      name={revoking === session.id ? "loader" : "trash"} 
                      size={20} 
                      color="#FF3B30" 
                    />
                  </button>
                )}
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

